<template>
  <div class="sidebar">
    <div class="sidebar-header">
      <div class="logo-area">
        <div class="logo-circle mail-logo">
          <span class="logo-text">MAIL</span>
        </div>
        <div class="company-info">
          <span class="company-name">{{ companyName }}</span>
        </div>
      </div>
    </div>

    <ul class="sidebar-menu">
      <li class="menu-item" :class="{ active: currentTab === 'mailOverview' }">
        <button @click="$emit('navigate', 'mailOverview')">
          <i class="fas fa-tachometer-alt"></i>
          <span>邮件概览</span>
        </button>
      </li>

      <li class="menu-item has-submenu" :class="{ active: isInternalMailActive, open: internalMailOpen }">
        <button @click="toggleInternalMail">
          <i class="fas fa-building"></i>
          <span>内邮</span>
          <span v-if="totalInternalUnread > 0" class="nav-badge">{{ totalInternalUnread > 99 ? '99+' : totalInternalUnread }}</span>
          <i class="fas fa-angle-down arrow"></i>
        </button>
        <ul class="submenu" v-show="internalMailOpen">
          <li><button :class="{ active: currentTab === 'mailInternal' }" @click="$emit('navigate', 'mailInternal')">收件箱 <span v-if="internalUnread > 0" class="sub-badge">{{ internalUnread }}</span></button></li>
          <li><button :class="{ active: currentTab === 'mailCompose' }" @click="$emit('navigate', 'mailCompose')">写邮件</button></li>
        </ul>
      </li>

      <li class="menu-item has-submenu" :class="{ active: isExternalMailActive, open: externalMailOpen }">
        <button @click="toggleExternalMail">
          <i class="fas fa-globe"></i>
          <span>外邮</span>
          <span v-if="totalExternalUnread > 0" class="nav-badge">{{ totalExternalUnread > 99 ? '99+' : totalExternalUnread }}</span>
          <i class="fas fa-angle-down arrow"></i>
        </button>
        <ul class="submenu" v-show="externalMailOpen">
          <li><button :class="{ active: currentTab === 'mailExternal' }" @click="$emit('navigate', 'mailExternal')">收件箱 <span v-if="externalUnread > 0" class="sub-badge">{{ externalUnread }}</span></button></li>
          <li><button :class="{ active: currentTab === 'mailExtSent' }" @click="$emit('navigate', 'mailExtSent')">已发送</button></li>
          <li><button :class="{ active: currentTab === 'mailExtCompose' }" @click="$emit('navigate', 'mailExtCompose')">写邮件</button></li>
        </ul>
      </li>

      <li class="menu-item" :class="{ active: currentTab === 'mailSettings' }">
        <button @click="$emit('navigate', 'mailSettings')">
          <i class="fas fa-cog"></i>
          <span>邮箱配置</span>
        </button>
      </li>
    </ul>

    <div class="sidebar-toggle" @click="toggleSidebar">
      <i class="fas fa-chevron-left"></i>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  currentTab: { type: String, default: 'mailOverview' }
})
defineEmits(['navigate'])

const companyName = ref('顾德智能')
const internalMailOpen = ref(true)
const externalMailOpen = ref(false)
const internalUnread = ref(5)
const externalUnread = ref(3)

const isInternalMailActive = computed(() => ['mailInternal', 'mailCompose'].includes(props.currentTab))
const isExternalMailActive = computed(() => ['mailExternal', 'mailExtCompose'].includes(props.currentTab))
const totalInternalUnread = computed(() => internalUnread.value)
const totalExternalUnread = computed(() => externalUnread.value)

const toggleInternalMail = () => { internalMailOpen.value = !internalMailOpen.value }
const toggleExternalMail = () => { externalMailOpen.value = !externalMailOpen.value }

const toggleSidebar = () => {
  const sidebar = document.querySelector('.sidebar')
  if (sidebar) sidebar.classList.toggle('collapsed')
  const wrapper = document.querySelector('#wrapper')
  if (wrapper) wrapper.classList.toggle('collapsed')
}
</script>

<style scoped>
.sidebar {
  width: 220px; min-height: 100vh; position: fixed; top: 0; left: 60px;
  z-index: 1000; background: #1e293b; transition: width 0.3s ease; overflow-y: auto; overflow-x: visible;
}
.sidebar.collapsed { width: 50px; }
.sidebar-header { padding: 12px; background: #0f172a; display: flex; align-items: center; }
.logo-area { display: flex; align-items: center; gap: 10px; }
.logo-circle { width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 10px rgba(0,0,0,0.3); }
.mail-logo { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
.logo-text { color: #fff; font-size: 12px; font-weight: 700; }
.company-info { display: flex; flex-direction: column; }
.company-name { color: #fff; font-size: 14px; font-weight: 600; white-space: nowrap; }
.sidebar.collapsed .company-info { display: none; }

.sidebar-menu { list-style: none; margin: 0; padding: 10px 0; }
.menu-item { position: relative; }
.menu-item > button, .submenu button {
  width: 100%; padding: 12px 20px; background: none; border: none; color: #94a3b8;
  cursor: pointer; display: flex; align-items: center; gap: 12px; font-size: 13px;
  transition: all 0.2s; text-align: left;
}
.menu-item > button:hover, .submenu button:hover { background: rgba(255,255,255,0.05); color: #e2e8f0; }
.menu-item.active > button { color: #fff; background: rgba(245,158,11,0.15); border-left: 3px solid #f59e0b; }
.menu-item > button i { width: 18px; text-align: center; font-size: 14px; }
.arrow { margin-left: auto; transition: transform 0.3s; font-size: 12px; }
.has-submenu.open > button .arrow { transform: rotate(180deg); }

.nav-badge { background: #ef4444; color: #fff; font-size: 10px; padding: 2px 6px; border-radius: 10px; min-width: 18px; text-align: center; font-weight: 600; margin-left: auto; }

.submenu { list-style: none; margin: 0; padding: 0; background: rgba(0,0,0,0.15); }
.submenu button { padding-left: 52px; font-size: 12px; }
.submenu button.active { color: #fbbf24; background: rgba(245,158,11,0.12); border-left: 3px solid #fbbf24; }
.sub-badge { background: #ef4444; color: #fff; font-size: 10px; padding: 1px 6px; border-radius: 10px; margin-left: auto; }

.sidebar-toggle { position: absolute; bottom: 20px; left: 0; right: 0; display: flex; justify-content: center; cursor: pointer; color: #6b7280; padding: 10px; transition: color 0.2s; }
.sidebar-toggle:hover { color: #fff; }
.sidebar-toggle i { font-size: 14px; }
</style>
